import { CommonModule } from '@angular/common';
import { Component, InputSignal, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { dateFormatPipe } from '@app/pipes/datepipe';
import { AlertComponent } from '@components/atoms/alert/alert.component';
import { MedpaceDatepickerComponent } from '@components/atoms/medpace-datepicker/medpace-datepicker.component';
import { CardMode } from '@components/card/card.component';
import { MdsAutocompleteComponent, MdsTextFieldComponent } from '@medpacesoftwaredevelopment/designsystem';
import { Caregiver, Patient } from '@models/patient';
import { CountryStateService } from '@services/state-management/country-state.service';
import { PersistentFormControl, PersistentFormGroup } from '@utility/persistent-forms';
import {
    equalOrLessThanTodayDateValidator,
    maxLengthValidator,
    moreThanTodayDateValidator,
    notWhitespaceValidator,
} from '@utility/utility.validators';
import { Moment } from 'moment';
import { defer } from 'rxjs';
export class PassportInfoFormGroup extends PersistentFormGroup<{
    number: PersistentFormControl<string>;
    country: PersistentFormControl<string>;
    issueDate: PersistentFormControl<Moment>;
    expirationDate: PersistentFormControl<Moment>;
}> {
    title: string;
    holder: Patient | Caregiver;

    public static create(title: string, holder: Patient | Caregiver) {
        const formGroup = new PassportInfoFormGroup(
            {
                number: new PersistentFormControl('', [notWhitespaceValidator(), maxLengthValidator(20)]),
                country: new PersistentFormControl(''),
                issueDate: new PersistentFormControl<Moment>(null, [equalOrLessThanTodayDateValidator]),
                expirationDate: new PersistentFormControl<Moment>(null, [moreThanTodayDateValidator]),
            },
            [
                (control: AbstractControl): ValidationErrors | null => {
                    const typedControl = control as PassportInfoFormGroup;
                    const values = Object.values(typedControl.value);
                    const someFieldHasValue = values.find((value) => value !== '' && value !== null) || false;
                    const allFieldsHaveValues = values.every((value) => value !== '' && value !== null);
                    if (someFieldHasValue && !allFieldsHaveValues)
                        return { requiredAllValues: 'Some fields have not been populated!' };
                    else return null;
                },
            ]
        );
        formGroup.title = title;
        formGroup.holder = holder;
        return formGroup;
    }
}
@Component({
    selector: 'medpace-passport-info',
    templateUrl: './passport-info.component.html',
    styleUrls: ['./passport-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        dateFormatPipe,
        AlertComponent,
        MedpaceDatepickerComponent,
        MdsTextFieldComponent,
        MdsAutocompleteComponent,
    ],
})
export class PassportInfoComponent {
    private readonly countryStateService = inject(CountryStateService);

    public readonly formGroup: InputSignal<PassportInfoFormGroup> = input.required<PassportInfoFormGroup>();
    public readonly mode: InputSignal<CardMode> = input.required<CardMode>();

    public readonly countries$ = defer(() => this.countryStateService.getCountriesStringArray());
}
